import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { readerApi, familyApi, chapterApi, reportApi } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import ProgressOverlay from '../components/ProgressOverlay';
import ReaderSelector from '../components/reporting/ReaderSelector';
import ChapterCalendarSelector from '../components/reporting/ChapterCalendarSelector';
import BookSelector from '../components/reporting/BookSelector';
import ReadingDetailsForm from '../components/reporting/ReadingDetailsForm';
import UndoModal from '../components/UndoModal';
import LogMoment from '../components/log/LogMoment';
import RawChapterReport from '../components/reporting/RawChapterReport';
import { hasSimplifiedSetup } from '../utils/userSetup';
import { localDateOnly } from '../utils/localDate';
import './ReportReadingPage.css';

const ReportReadingPage = () => {
  const { preferences } = useAuth();
  const competitiveMode = Boolean(preferences?.competitiveMode);
  const asyncUpdateNote = (isAsync) => {
    if (!isAsync) return '';
    return competitiveMode
      ? ' Points and plan updates are processing in the background.'
      : ' Plan updates are processing in the background.';
  };
  const [readers, setReaders] = useState([]);
  const [families, setFamilies] = useState([]);
  const [books, setBooks] = useState({ Old: [], New: [] });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submissionProgress, setSubmissionProgress] = useState({
    stage: '',
    current: 0,
    total: 0,
    message: ''
  });
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [showUndoModal, setShowUndoModal] = useState(false);
  // WS3: the post-log moment, shown for chapter reports the same way QuickLog does.
  const [moment, setMoment] = useState(null);
  const [momentReportIds, setMomentReportIds] = useState([]);

  // Form state
  const [reportMode, setReportMode] = useState('chapters'); // 'chapters' or 'books'
  const [selectedReaders, setSelectedReaders] = useState([]);
  const [readerMode, setReaderMode] = useState('primary'); // 'primary', 'family', 'specific'
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [selectedChapters, setSelectedChapters] = useState([]);
  const [selectedBooks, setSelectedBooks] = useState([]);
  const [readDate, setReadDate] = useState(localDateOnly());
  const [notes, setNotes] = useState('');
  const [readingDetails, setReadingDetails] = useState({});
  const [showReadingDetails, setShowReadingDetails] = useState(false);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [readersRes, familiesRes, booksRes] = await Promise.all([
        readerApi.getReaders(),
        familyApi.getFamilies(),
        chapterApi.getBooks()
      ]);

      setReaders(readersRes.data.readers || []);
      setFamilies(familiesRes.data.families || []);
      setBooks(booksRes.data.books || { Old: [], New: [] });

      // Set primary reader as default
      const primaryReader = readersRes.data.readers?.find(r => r.isPrimary);
      if (primaryReader) {
        setSelectedReaders([primaryReader]);
      }

      setError(null);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load the log form. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReaderModeChange = (mode) => {
    setReaderMode(mode);
    setSelectedReaders([]);
    setSelectedFamily(null);

    if (mode === 'primary') {
      const primaryReader = readers.find(r => r.isPrimary);
      if (primaryReader) {
        setSelectedReaders([primaryReader]);
      }
    }
  };

  const handleFamilySelect = (family) => {
    setSelectedFamily(family);
    setSelectedReaders(family?.readers || []);
  };

  const handleReaderToggle = (reader) => {
    setSelectedReaders(prev => {
      const isSelected = prev.some(r => r.id === reader.id);
      if (isSelected) {
        return prev.filter(r => r.id !== reader.id);
      } else {
        return [...prev, reader];
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedReaders.length === 0) {
      setError('Please select at least one reader.');
      return;
    }

    if (reportMode === 'chapters' && selectedChapters.length === 0) {
      setError('Please select at least one chapter.');
      return;
    }

    if (reportMode === 'books' && selectedBooks.length === 0) {
      setError('Please select at least one book.');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      setSuccessMessage('');

      let reportData = {
        readDate,
        notes: notes.trim() || undefined,
        ...readingDetails
      };

      if (reportMode === 'books') {
        // Calculate total reports for progress tracking
        const totalReportsToCreate = selectedBooks.length * selectedReaders.length;
        const estimatedTimePerBook = Math.max(2, Math.ceil(selectedReaders.length * 0.5)); // Estimate 0.5s per reader per book, minimum 2s
        setSubmissionProgress({
          stage: 'books',
          current: 0,
          total: selectedBooks.length,
          totalReports: totalReportsToCreate,
          estimatedDuration: selectedBooks.length * estimatedTimePerBook,
          message: `Preparing to log ${selectedBooks.length} book(s) for ${selectedReaders.length} reader(s)...`
        });

        // PERFORMANCE OPTIMIZATION: Start a session for multi-book operations
        const sessionId = `books-${Date.now()}`;
        let sessionStarted = false;
        
        if (selectedBooks.length > 1) {
          try {
            await reportApi.startSession(sessionId, selectedBooks.length);
            sessionStarted = true;
            console.log(`Started session for ${selectedBooks.length} books`);
          } catch (error) {
            console.warn('Failed to start session, continuing without:', error);
          }
        }

        // For books, we'll report one book at a time for better feedback
        const results = [];
        try {
          for (let i = 0; i < selectedBooks.length; i++) {
            const book = selectedBooks[i];
            const bookReportData = { 
              ...reportData, 
              book,
              sessionId: sessionStarted ? sessionId : undefined
            };
            
            setSubmissionProgress(prev => ({
              ...prev,
              current: i + 1,
              message: `Logging ${book} (${i + 1} of ${selectedBooks.length})...`
            }));
            
            if (readerMode === 'family' && selectedFamily) {
              const result = await reportApi.createFamilyReport(selectedFamily.id, bookReportData);
              results.push(result.data);
            } else {
              // Multiple individual readers
              if (selectedReaders.length === 1) {
                bookReportData.readerId = selectedReaders[0].id;
              } else {
                bookReportData.readerIds = selectedReaders.map(r => r.id);
              }
              const result = await reportApi.createReport(bookReportData);
              results.push(result.data);
            }
          }
        } finally {
          // PERFORMANCE OPTIMIZATION: End the session
          if (sessionStarted) {
            try {
              await reportApi.endSession(sessionId);
              console.log(`Ended session for ${selectedBooks.length} books`);
            } catch (error) {
              console.warn('Failed to end session:', error);
            }
          }
        }
        
        const totalReports = results.reduce((sum, result) => sum + (result.summary?.reportsCreated || 0), 0);
        const totalSkipped = results.reduce((sum, result) => sum + (result.summary?.skipped || 0), 0);
        const hasAsyncProcessing = results.some(result => result.summary?.processingMode === 'asynchronous');
        
        setSuccessMessage(
          `Successfully logged ${selectedBooks.length} book(s) for ${selectedReaders.length} reader(s). ` +
          `Logged ${totalReports} chapter${totalReports === 1 ? '' : 's'}${totalSkipped > 0 ? `, skipped ${totalSkipped} duplicates` : ''}.` +
          `${asyncUpdateNote(hasAsyncProcessing)}`
        );
      } else {
        // Chapter reporting
        const totalReportsToCreate = selectedChapters.length * selectedReaders.length;
        const estimatedTimeForChapters = Math.max(3, Math.ceil(totalReportsToCreate * 0.1)); // Estimate 0.1s per report, minimum 3s
        setSubmissionProgress({
          stage: 'chapters',
          current: 0,
          total: totalReportsToCreate,
          totalReports: totalReportsToCreate,
          estimatedDuration: estimatedTimeForChapters,
          message: `Logging ${totalReportsToCreate} chapter${totalReportsToCreate === 1 ? '' : 's'} for ${selectedReaders.length} reader(s)...`
        });

        if (readerMode === 'family' && selectedFamily) {
          reportData.chapterIds = selectedChapters;
          const result = await reportApi.createFamilyReport(selectedFamily.id, reportData);
          const asyncProcessing = result.data.summary?.processingMode === 'asynchronous';
          setSuccessMessage(
            result.data.message + 
            asyncUpdateNote(asyncProcessing)
          );
          setMoment(result.data.moment || null);
          setMomentReportIds((result.data.reports || []).map(r => r.id));
        } else {
          if (selectedReaders.length === 1) {
            reportData.readerId = selectedReaders[0].id;
          } else {
            reportData.readerIds = selectedReaders.map(r => r.id);
          }
          reportData.chapterIds = selectedChapters;
          const result = await reportApi.createReport(reportData);
          const asyncProcessing = result.data.summary?.processingMode === 'asynchronous';
          setSuccessMessage(
            result.data.message +
            asyncUpdateNote(asyncProcessing)
          );
          setMoment(result.data.moment || null);
          setMomentReportIds((result.data.reports || []).map(r => r.id));
        }
      }

      // Reset form
      setSelectedChapters([]);
      setSelectedBooks([]);
      setNotes('');
      
    } catch (err) {
      console.error('Failed to submit report:', err);
      setError(err.response?.data?.error || 'Failed to log that reading. Please try again.');
    } finally {
      setSubmitting(false);
      setSubmissionProgress({
        stage: '',
        current: 0,
        total: 0,
        message: ''
      });
    }
  };

  const handleUndoSuccess = (message, summary) => {
    setSuccessMessage('');
    setError(null);
    // Show a different success message for undo
    setSuccessMessage(`✅ ${message}. Removed logs for: ${summary.readers.join(', ')}`);
  };

  if (loading) {
    return (
      <div className="report-page loading">
        <LoadingSpinner size="large" message="Loading..." />
      </div>
    );
  }

  return (
    <div className="report-page">
      <div className="report-header">
        <h1>Log reading details</h1>
        <p>
          {hasSimplifiedSetup(readers, families) 
            ? "Record chapters or books you've read" 
            : "Record chapters or books you or your family have read"
          }
        </p>
      </div>

      {readers.length === 0 && (
        <div className="error-message">
          <p>
            Add a reader under Family → Readers before logging what you read.{' '}
            <Link to="/family#readers">Go to Family → Readers</Link>
          </p>
        </div>
      )}

      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}

      {successMessage && (
        <div className="success-message">
          <p>{successMessage}</p>
          <button 
            className="undo-link"
            onClick={() => setShowUndoModal(true)}
            type="button"
          >
            Caught a mistake? Click here to undo.
          </button>
        </div>
      )}

      <form onSubmit={handleSubmit} className={`report-form ${submitting ? 'submitting' : ''}`}>
        {/* Reader Selection - Only show if user has additional readers or family */}
        {(() => {
          const isSimplifiedSetup = hasSimplifiedSetup(readers, families);
          return !isSimplifiedSetup ? (
            <div className="form-section">
              <h2>Who read?</h2>
              <ReaderSelector
                readers={readers}
                families={families}
                readerMode={readerMode}
                selectedReaders={selectedReaders}
                selectedFamily={selectedFamily}
                onReaderModeChange={handleReaderModeChange}
                onFamilySelect={handleFamilySelect}
                onReaderToggle={handleReaderToggle}
              />
            </div>
          ) : null;
        })()}

        {/* Report Mode Selection */}
        <div className="form-section">
          <h2>What did you read?</h2>
          <div className="report-mode-selector">
            <label className="mode-option">
              <input
                type="radio"
                name="reportMode"
                value="chapters"
                checked={reportMode === 'chapters'}
                onChange={(e) => setReportMode(e.target.value)}
              />
              <span>Select chapters</span>
              <small>Choose specific chapter ranges</small>
            </label>
            <label className="mode-option">
              <input
                type="radio"
                name="reportMode"
                value="books"
                checked={reportMode === 'books'}
                onChange={(e) => setReportMode(e.target.value)}
              />
              <span>Select books</span>
              <small>Choose entire books</small>
            </label>
            <label className="mode-option">
              <input
                type="radio"
                name="reportMode"
                value="raw"
                checked={reportMode === 'raw'}
                onChange={(e) => setReportMode(e.target.value)}
              />
              <span>Bulk log</span>
              <small>Just the total number</small>
            </label>
          </div>
        </div>

        {/* Raw Chapter Report */}
        {reportMode === 'raw' && (
          <div className="form-section">
            <RawChapterReport 
              readers={readers}
              onSuccess={(report) => {
                setSuccessMessage(`Successfully logged ${report.chapterCount} chapter(s)!`);
                setSubmitting(false);
              }}
            />
          </div>
        )}

        {/* Chapter/Book Selection - Only show if not raw mode */}
        {reportMode !== 'raw' && (
        <div className="form-section">
          {reportMode === 'chapters' ? (
            <ChapterCalendarSelector
              selectedChapters={selectedChapters}
              onChaptersChange={setSelectedChapters}
            />
          ) : (
            <BookSelector
              books={books}
              selectedBooks={selectedBooks}
              onBooksChange={setSelectedBooks}
            />
          )}
        </div>
        )}

        {/* Date and Notes - Only show if not raw mode */}
        {reportMode !== 'raw' && (
        <div className="form-section">
          <h3>Additional details</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="readDate">Date</label>
              <input
                type="date"
                id="readDate"
                value={readDate}
                onChange={(e) => setReadDate(e.target.value)}
                max={localDateOnly()}
              />
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="notes">Notes (optional)</label>
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any thoughts or reflections..."
              maxLength={1000}
              rows={3}
            />
          </div>
        </div>
        )}

        {/* Reading Details - Only show if not raw mode */}
        {reportMode !== 'raw' && (
        <ReadingDetailsForm
          formData={readingDetails}
          onChange={setReadingDetails}
          showDetails={showReadingDetails}
          onToggleDetails={() => setShowReadingDetails(!showReadingDetails)}
        />
        )}

        {/* Submit - Only show if not raw mode */}
        {reportMode !== 'raw' && (
        <div className="form-actions">
          <button
            type="submit"
            disabled={submitting || selectedReaders.length === 0 || 
              (reportMode === 'chapters' && selectedChapters.length === 0) ||
              (reportMode === 'books' && selectedBooks.length === 0)
            }
            className={`submit-button ${submitting ? 'submitting' : ''}`}
          >
            {submitting ? (
              <>
                <div className="button-spinner"></div>
                Logging...
              </>
            ) : (
              'Log reading'
            )}
          </button>
          {submitting && (
            <div className="submission-hint">
              <p>Large logs may take a few minutes. Please don't close this window.</p>
            </div>
          )}
        </div>
        )}
      </form>

      {/* Progress Overlay */}
      <ProgressOverlay
        isVisible={submitting}
        progress={submissionProgress}
      />

      {/* Post-log moment */}
      {moment && (
        <LogMoment
          moment={moment}
          onClose={() => setMoment(null)}
          onUndo={() => {
            setMoment(null);
            setShowUndoModal(true);
          }}
          onSaveReflection={momentReportIds.length > 0
            ? (text) => Promise.all(
                momentReportIds.map(id => reportApi.updateReport(id, { notes: text }))
              )
            : undefined}
        />
      )}

      {/* Undo Modal */}
      <UndoModal
        isOpen={showUndoModal}
        onClose={() => setShowUndoModal(false)}
        onUndoSuccess={handleUndoSuccess}
      />
    </div>
  );
};

export default ReportReadingPage;
